import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { correctFoundStockDeclaration } from '../../services/foundStockServices'
import { newRequestId } from './foundStockRows'
import { FoundStockDialogShell } from './FoundStockDialogShell'
import { FrozenProposalEventLink } from './FrozenProposalEventLink'
import { useSettlementRequest } from './useSettlementRequest'

const T = 'found_stock.correct_dialog'
const REASON_MAX = 500
const QUANTITY_MAX = 1000

export const CorrectFoundStockDialog = ({
  open,
  warehouseId,
  group,
  product = null,
  onSuccess = () => {},
  onCancel = () => {},
  onGateOff = () => {},
  onStale = () => {},
}) => {
  const { i18n } = useTranslation()
  const [quantity, setQuantity] = useState('')
  const [reason, setReason] = useState('')
  const [requestId, setRequestId] = useState(null)
  const { busy, errorMessage, errorCoverageEventId, reset, run } = useSettlementRequest({ onSuccess, onGateOff, onStale })

  // The queue refetch that a failed correction triggers hands back a NEW group object for the SAME
  // declaration. Re-seeding on that would wipe the mandatory reason the operator just typed and
  // replace their quantity with the live one, which then commits as an absolute quantity_after
  // nobody chose. So the effect keys on the declaration identity only and reads the payload
  // through a ref it does not depend on.
  const groupRef = useRef(group)
  groupRef.current = group
  const declarationId = group?.declaration_id

  useEffect(() => {
    if (open) {
      setQuantity(String(groupRef.current?.quantity ?? ''))
      setReason('')
      setRequestId(newRequestId())
    } else {
      reset()
      setReason('')
    }
  }, [open, declarationId, reset])

  // The request id identifies an intent: same values replay, changed values are a new request.
  const changeQuantity = value => {
    setQuantity(value)
    setRequestId(newRequestId())
  }
  const changeReason = value => {
    setReason(value)
    setRequestId(newRequestId())
  }

  const parsed = Number(quantity)
  const quantityValid = quantity !== '' && Number.isInteger(parsed) && parsed >= 0 && parsed <= QUANTITY_MAX
  const trimmedReason = reason.trim()
  const reasonValid = trimmedReason.length > 0 && trimmedReason.length <= REASON_MAX

  const onConfirm = async () => {
    if (!quantityValid || !reasonValid || !group) return
    const { ok, outcome } = await run(
      () => correctFoundStockDeclaration(warehouseId, group.declaration_id, { request_id: requestId, quantity_after: parsed, reason: trimmedReason }),
      result => i18n.t(`${T}.${result.replayed ? 'replayed' : 'success'}`, { before: result.quantity_before, after: result.quantity_after }),
    )
    // The stored intent is the identity: a conflicting reuse means this id is burnt, mint a new one.
    if (!ok && outcome?.code === 'IDEMPOTENCY_CONFLICT') setRequestId(newRequestId())
  }

  return (
    <FoundStockDialogShell
      open={open}
      title={i18n.t(`${T}.title`)}
      summary={i18n.t(`${T}.summary`, { id: group?.declaration_id, location: group?.location_code, quantity: group?.quantity })}
      product={product}
      productId={group?.product_id}
      busy={busy}
      errorMessage={errorMessage}
      errorAction={<FrozenProposalEventLink warehouseId={warehouseId} eventId={errorCoverageEventId} />}
      confirmLabel={i18n.t(`${T}.confirm`)}
      cancelLabel={i18n.t(`${T}.cancel`)}
      confirmDisabled={!quantityValid || !reasonValid}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <label className="block text-sm font-medium text-gray-700" htmlFor="found-stock-correct-quantity">
        {i18n.t(`${T}.quantity_label`)}
      </label>
      <input
        id="found-stock-correct-quantity"
        type="number"
        min={0}
        max={QUANTITY_MAX}
        step={1}
        value={quantity}
        onChange={e => changeQuantity(e.target.value)}
        disabled={busy}
        className="mt-1 block w-32 rounded-md border-gray-300 shadow-sm text-sm"
      />
      <p className="text-xs text-gray-500 mt-1">{i18n.t(`${T}.quantity_hint`)}</p>
      {quantity !== '' && !quantityValid && <p className="text-xs text-red-600 mt-1">{i18n.t(`${T}.validation_quantity`)}</p>}
      <label className="block text-sm font-medium text-gray-700 mt-4" htmlFor="found-stock-correct-reason">
        {i18n.t(`${T}.reason_label`)}
      </label>
      <textarea
        id="found-stock-correct-reason"
        rows={3}
        maxLength={REASON_MAX}
        value={reason}
        onChange={e => changeReason(e.target.value)}
        disabled={busy}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-sm"
      />
      <div className="flex justify-between text-xs text-gray-500 mt-1">
        <span>{i18n.t(`${T}.reason_hint`)}</span>
        <span>{i18n.t(`${T}.reason_counter`, { count: reason.length })}</span>
      </div>
    </FoundStockDialogShell>
  )
}
